import React, {useCallback, useEffect, useState} from 'react';
import classNames from 'classnames';
import { IconButton } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/shared/header';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import PageHeader from '../../components/shared/page-header';
import PageBody from '../../components/shared/page-body';
import PageFooter from '../../components/shared/page-footer';
import PageTableContainer from '../../components/shared/page-table-container';
import TiminatorTableV2 from '../../components/shared/table-v2';
import IconTextLarge from '../../components/shared/large-icon-text';
import {SVGS} from '../../assets/images';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {deleteEmployee, getEmployees} from '../../features/employees';
import { Employee } from '../../features/employees/types';
import {Lx} from '../../i18n/consts';
import {useInfinitePagination} from '../../hooks/useInfinitePagination';
import CreateNewButtonV2 from '../../components/shared/create-new-button-v2';
import { NAVIGATION_ROUTES } from '../../routes/NavigationRoute';
import { useEmployeesTable } from './hook/useEmployeesTable';
import styles from './employees.module.scss';
import { DeleteDialog } from '../../components/shared/delete-dialog';
import { useIntl } from 'react-intl';
import { EmployeeFilter, DEFAULT_EMPLOYEE_FILTER_STATE } from './components/employee-filter';
import { canManageEmployees } from '../../features/profile/entityPermissions';

export default function EmployeesPage() {
    const dispatch = useAppDispatch();
    const isMobile = useMediaQuery({ maxWidth: 1000 });
    const navigate = useNavigate();
    const intl = useIntl();

    const employeesState = useAppSelector(store => store.employees);
    const profile = useAppSelector(store => store.profile);
    const [open, setOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const canEditEmployees = canManageEmployees(profile);

    const hasMore = employeesState.items.length < employeesState.total;

    const fetchEmployeesPage = useCallback((page: number) => {
        return dispatch(getEmployees(page)).unwrap();
    }, [dispatch]);

    const {
        loadInitial,
        loadNext,
        isLoadingMore,
        isLoadingInitial,
        reset
    } = useInfinitePagination({
        hasMore,
        fetchPage: fetchEmployeesPage,
    });

    const [search, setSearch] = useState('');
    const [selectedStatus, setSelectedStatus] = useState<number>(-1);
    const [mobileView, setMobileView] = useState<'list' | 'filter'>('list');

    useEffect(() => {
        loadInitial().catch(() => { /* handled elsewhere */ });
    }, [loadInitial]);

    useEffect(() => {
        if (!isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    const tableSettings = useEmployeesTable({
        search,
        selectedStatus,
        isMobile,
        canDelete: canEditEmployees,
        onDeleteClick: (employee) => {
            setSelectedEmployee(employee);
            setOpen(true);
        },
    });

    const handleLoadMore = useCallback(() => {
        loadNext().catch(() => { /* handled elsewhere */ });
    }, [loadNext]);

    const handleCreateClick = useCallback(() => {
        if (!canEditEmployees) {
            return;
        }

        navigate(NAVIGATION_ROUTES.CREATE_EMPLOYEE);
    }, [canEditEmployees, navigate]);

    const handleRowClick = useCallback((employee: Employee) => {
        if (!canEditEmployees) {
            return;
        }

        navigate(NAVIGATION_ROUTES.EDIT_EMPLOYEE, {
            state: {
                employee,
            },
        });
    }, [canEditEmployees, navigate]);

    const reloadEmployees = useCallback(async () => {
        reset();
        setMobileView('list');
        try {
            await loadInitial();
        } catch {
            // handled elsewhere
        }
    }, [loadInitial, reset]);

    const isEmpty = !isLoadingInitial && tableSettings.isEmpty;
    const showMobileFilterToggle = isMobile && !isEmpty;
    const isMobileFilterActive = isMobile && mobileView === 'filter';

    const handleMobileFilterClick = () => {
        setMobileView(prev => (prev === 'list' ? 'filter' : 'list'));
    };

    const handleMobileFilterClose = useCallback(() => {
        setMobileView('list');
    }, []);

    const handleFilterChange = useCallback(({ search: nextSearch, selectedStatus: nextStatus }: { search: string; selectedStatus: number }) => {
        setSearch(nextSearch);
        setSelectedStatus(nextStatus);
    }, []);

    return (
        <>
            <PageWrapperV2>
                <PageHeader>
                    <div className={styles.header}>
                        <div className={styles.topRow}>
                            <div className={styles.title}>
                                <Header headerLocalizationId={Lx.General.EMPLOYEES} loading={isLoadingInitial}>
                                    <div className={styles.employeesCount}>{tableSettings.data?.length ?? 0}</div>
                                </Header>
                            </div>
                            <div className={styles.actions}>
                                {showMobileFilterToggle ? (
                                    <IconButton
                                        aria-label='filterIconButton'
                                        onClick={() => {
                                            if (isMobileFilterActive) {
                                                handleFilterChange(DEFAULT_EMPLOYEE_FILTER_STATE);
                                            }
                                            handleMobileFilterClick();
                                        }}>
                                        <SVGS.FilterIcon className={classNames({[styles.active]: isMobileFilterActive})} />
                                    </IconButton>
                                ) : null}
                                {!isMobile ? (
                                    canEditEmployees ? <CreateNewButtonV2 onClick={handleCreateClick} /> : null
                                ) : null}
                            </div>
                        </div>
                    </div>
                </PageHeader>
                <PageBody justifyContent={isEmpty ? 'center' : 'flex-start'} alignItems='stretch'>
                    {!isEmpty ? (
                        <EmployeeFilter
                            search={search}
                            selectedStatus={selectedStatus}
                            isMobile={isMobile}
                            mobileView={mobileView}
                            onFilterChange={handleFilterChange}
                            onMobileClose={handleMobileFilterClose}>
                            <PageTableContainer>
                                <TiminatorTableV2
                                    columns={tableSettings.columns}
                                    data={tableSettings.data}
                                    desktopMinWidth={1115}
                                    hasMore={hasMore}
                                    onLoadMore={handleLoadMore}
                                    onRowClick={canEditEmployees ? handleRowClick : undefined}
                                    isLoadingMore={isLoadingMore}
                                />
                            </PageTableContainer>
                        </EmployeeFilter>
                    ) : (
                        <IconTextLarge
                            text={intl.formatMessage({ id: Lx.Employee.NO_EMPLOYEES })}
                            subtext={intl.formatMessage({ id: Lx.Employee.CREATE_AND_USE_EMPLOYEE })}
                            icon={<SVGS.EmployeesIcon />}
                        />
                    )}
                </PageBody>
                {isMobile && mobileView === 'list' && canEditEmployees &&
                    <PageFooter>
                        <CreateNewButtonV2 onClick={handleCreateClick} />
                    </PageFooter>
                }
            </PageWrapperV2>
            <DeleteDialog
                open={open}
                onCancel={() => { setOpen(false); setSelectedEmployee(null); }}
                onConfirmDelete={() => {
                    if (selectedEmployee) {
                        dispatch(deleteEmployee(selectedEmployee.id)).unwrap().then(() => {
                            reloadEmployees();
                        });
                    }
                    setOpen(false);
                    setSelectedEmployee(null);
                }}
                deleteHeader={intl.formatMessage({ id: Lx.General.DELETE })}
                deleteMessage={`${intl.formatMessage({ id: Lx.General.DELETE })} ${selectedEmployee ? `${selectedEmployee.firstName ?? ''} ${selectedEmployee.lastName ?? ''}`.trim() : ''}?`}
                title={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
            />
        </>
    );
}
